import React, { useState, lazy } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Instagram, Facebook, Send } from 'lucide-react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/Button';
export function ContactPage() {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormState({
      ...formState,
      [e.target.name]: e.target.value
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      console.log('Message submitted:', formState);
      setIsSubmitting(false);
      setIsSuccess(true);
      setFormState({
        name: '',
        email: '',
        phone: '',
        message: ''
      });
    }, 1500);
  };
  const inputClasses = 'w-full bg-navy-900 border-2 border-navy-700 text-white px-4 py-3 focus:border-ocean-500 focus:outline-none focus:ring-1 focus:ring-ocean-500 transition-all duration-300 placeholder-gray-500 rounded-none';
  const labelClasses = 'block text-gold-500 font-heading font-bold text-sm uppercase tracking-wide mb-2';
  return <div className="min-h-screen bg-navy-900 text-white selection:bg-gold-500 selection:text-navy-900 flex flex-col" data-id="element-255">
      <Navigation data-id="element-256" />

      <main className="flex-grow pt-24 pb-16" data-id="element-257">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-258">
          <div className="text-center mb-16" data-id="element-259">
            <h1 className="font-heading font-bold text-4xl md:text-5xl text-white mb-4" data-id="element-260">
              Contact Us
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto" data-id="element-261">
              We'd love to hear from you! Contact us for reservations,
              enquiries, or general information.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-262">
            {/* Contact Info & Map */}
            <div className="space-y-8" data-id="element-263">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-264">
                <a href="tel:+353873307277" className="bg-navy-800 p-6 border border-navy-700 hover:border-gold-500 transition-all duration-300 group" data-id="element-265">
                  <div className="bg-navy-900 w-12 h-12 flex items-center justify-center rounded-full mb-4 text-gold-500 group-hover:scale-110 transition-transform" data-id="element-266">
                    <Phone className="w-6 h-6" data-id="element-267" />
                  </div>
                  <h3 className="font-heading font-bold text-lg text-white mb-1" data-id="element-268">
                    Call Us
                  </h3>
                  <p className="text-gray-400 group-hover:text-white transition-colors" data-id="element-269">
                    +353 87 330 7277
                  </p>
                </a>

                <a href="mailto:thedockwall@gmail.com" className="bg-navy-800 p-6 border border-navy-700 hover:border-gold-500 transition-all duration-300 group" data-id="element-270">
                  <div className="bg-navy-900 w-12 h-12 flex items-center justify-center rounded-full mb-4 text-gold-500 group-hover:scale-110 transition-transform" data-id="element-271">
                    <Mail className="w-6 h-6" data-id="element-272" />
                  </div>
                  <h3 className="font-heading font-bold text-lg text-white mb-1" data-id="element-273">
                    Email Us
                  </h3>
                  <p className="text-gray-400 group-hover:text-white transition-colors" data-id="element-274">
                    thedockwall@gmail.com
                  </p>
                </a>
              </div>

              <div className="bg-navy-800 p-6 border border-navy-700" data-id="element-275">
                <h3 className="font-heading font-bold text-xl text-white mb-4 flex items-center gap-2" data-id="element-276">
                  <MapPin className="w-5 h-5 text-gold-500" data-id="element-277" /> Find Us
                </h3>
                <div className="aspect-video w-full bg-navy-900 mb-4 overflow-hidden border border-navy-700 relative" data-id="element-278">
                  {/* Google Maps Embed */}
                  <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3151.835434509374!2d144.9537353153169!3d-37.816279742021665!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6ad642af0f11fd81%3A0xf577d6a32f7f1f81!2sFederation+Square!5e0!3m2!1sen!2sau!4v1538726824982" width="100%" height="100%" style={{
                  border: 0
                }} allowFullScreen loading="lazy" title="The Dock Wall Location" className="grayscale hover:grayscale-0 transition-all duration-500" data-id="element-279"></iframe>
                </div>
                <Button variant="outline" className="w-full" onClick={() => window.open('https://google.com/maps/dir/24.9890688,67.129867/51.55824,-9.14119/@30.7982906,-14.2190368,10762096m/data=!3m2!1e3!4b1!4m4!4m3!1m1!4e1!1m0', '_blank')} data-id="element-280">
                  Get Directions
                </Button>
              </div>

              <div className="flex justify-center gap-6 pt-4" data-id="element-281">
                <a href="https://www.instagram.com/thedockwall" target="_blank" rel="noopener noreferrer" className="bg-navy-800 p-4 rounded-full text-white hover:bg-gold-500 hover:text-navy-900 transition-all duration-300 hover:scale-110" data-id="element-282">
                  <Instagram className="w-6 h-6" data-id="element-283" />
                </a>
                <a href="https://www.facebook.com/thedockwall" target="_blank" rel="noopener noreferrer" className="bg-navy-800 p-4 rounded-full text-white hover:bg-gold-500 hover:text-navy-900 transition-all duration-300 hover:scale-110" data-id="element-284">
                  <Facebook className="w-6 h-6" data-id="element-285" />
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-navy-800 p-8 md:p-12 border border-navy-700 shadow-2xl relative overflow-hidden h-fit" data-id="element-286">
              <div className="absolute top-0 right-0 w-full h-2 bg-gradient-to-l from-gold-500 via-ocean-500 to-transparent" data-id="element-287" />

              <h2 className="font-heading font-bold text-3xl text-white mb-6" data-id="element-288">
                Send a Message
              </h2>

              {isSuccess ? <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} className="text-center py-12" data-id="element-289">
                  <div className="w-16 h-16 bg-green-500/20 text-green-500 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-290">
                    <Send className="w-8 h-8" data-id="element-291" />
                  </div>
                  <h3 className="text-xl font-heading font-bold text-white mb-4" data-id="element-292">
                    Message Sent
                  </h3>
                  <p className="text-gray-300 mb-8" data-id="element-293">
                    Thank you for contacting The Dock Wall. We will get back to
                    you shortly.
                  </p>
                  <Button variant="primary" onClick={() => setIsSuccess(false)} data-id="element-294">
                    Send Another Message
                  </Button>
                </motion.div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-295">
                  <div data-id="element-296">
                    <label htmlFor="name" className={labelClasses} data-id="element-297">
                      Full Name
                    </label>
                    <input type="text" id="name" name="name" required className={inputClasses} value={formState.name} onChange={handleChange} data-id="element-298" />
                  </div>

                  <div data-id="element-299">
                    <label htmlFor="email" className={labelClasses} data-id="element-300">
                      Email Address
                    </label>
                    <input type="email" id="email" name="email" required className={inputClasses} value={formState.email} onChange={handleChange} data-id="element-301" />
                  </div>

                  <div data-id="element-302">
                    <label htmlFor="phone" className={labelClasses} data-id="element-303">
                      Phone Number{' '}
                      <span className="text-gray-500 font-normal normal-case ml-1" data-id="element-304">
                        (Optional)
                      </span>
                    </label>
                    <input type="tel" id="phone" name="phone" className={inputClasses} value={formState.phone} onChange={handleChange} data-id="element-305" />
                  </div>

                  <div data-id="element-306">
                    <label htmlFor="message" className={labelClasses} data-id="element-307">
                      Message
                    </label>
                    <textarea id="message" name="message" required rows={5} className={inputClasses} value={formState.message} onChange={handleChange} data-id="element-308" />
                  </div>

                  <Button type="submit" variant="primary" size="lg" className="w-full" disabled={isSubmitting} data-id="element-309">
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>}
            </div>
          </div>
        </motion.div>
      </main>

      <Footer data-id="element-310" />
    </div>;
}